import Head from 'next/head'
import { FunctionComponent, useContext } from 'react'
import styled, { ThemeContext } from 'styled-components'

import HorizontalRule from 'components/GlobalStyles/Hr'
import HeroWithContentOverlay from 'components/HeroWithContentOverlay'
import RoundedButton from 'components/RoundedButton'
import FlaredArrow from 'components/SVG/FlaredArrow'
import Spacer from 'components/Spacer'

import { QueryParamsContext } from 'services/contexts'
import { useCloseHeaderModal, useWindowSize } from 'services/hooks'

const PageContainer = styled.div`
  margin: 0 auto;
  max-width: ${props => props.theme.metrics.contentMaxWidth}px;
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    padding: 0 15px;
  }
`

const FilledContainer = styled.div`
  background: ${props => props.theme.colors.grey2};
  padding: 10px ${props => props.theme.metrics.gutterPhone}px;
`

const Container = styled.div`
  margin: 0 ${props => props.theme.metrics.gutterPhone}px;
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    margin: 0;
  }
`

interface IBackgroundCoverProps {
  imgSrc: string
  height?: number
}
const BackgroundCover = styled.div<IBackgroundCoverProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 70vh;
  ${props => props.height && `height: ${props.height}px;`}
  background: center / cover no-repeat url(${props => props.imgSrc});
`

const Banner = styled.div`
  background-color: ${props => props.theme.colors.transparentWhiteAlpha87};
  border-radius: 30px;
  padding: 20px;
  margin: auto 30px;

  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    padding: 50px;
  }
`

const BannerText = styled.h1`
  font-weight: 500;
  font-size: 32px;
  line-height: 52px;
  text-align: center;
  margin: 0;
  padding: 0;
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    font-size: 42px;
  }
`

const AccentTextBanner = styled.div`
  background: ${props => props.theme.colors.blue};
  color: ${props => props.theme.colors.white};
  font-weight: 900;
  font-size: 22px;
  line-height: 32px;
  padding: 15px;
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    text-align: center;
  }
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    font-size: 28px;
  }
`

const H2 = styled.h2`
  font-weight: 900;
  font-size: 22px;
  line-height: 32px;
  margin: 15px 0;
`

const H3 = styled.h3`
  font-weight: 800;
  font-size: 18px;
  line-height: 22px;
`

const H4 = styled.h4`
  font-weight: 800;
  font-size: 24px;
  line-height: 42px;
  text-align: center;
  margin: 0;
`

const H5 = styled.h5`
  font-weight: 400;
  font-size: 18px;
  line-height: 25px;
  text-align: center;
  margin: 0;
`
interface IParagraphProps {
  bold?: boolean
}
const Paragraph = styled.p<IParagraphProps>`
  font-size: 16px;
  line-height: 24px;
  font-weight: ${props => (props.bold ? 900 : 400)};
`

const ResultsContainer = styled.div`
  width: 100%;
  margin: 0 auto;
`

const BoxContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    flex-direction: row;
  }
`

const Box = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  height: 125px;
  margin: 10px;
  padding: 20px;
  background: ${props => props.theme.colors.grey2};

  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    max-width: 250px;
  }
`

const BoldText = styled.div`
  font-weight: 900;
  font-size: 22px;
  line-height: 32px;
`

const MaxWidthContainer = styled(Container)`
  display: flex;
  justify-content: center;
`

const WhyHomelister: FunctionComponent = () => {
  // cleanup if user navigated from header modal
  useCloseHeaderModal()

  const theme = useContext(ThemeContext)
  const { params: query } = useContext(QueryParamsContext)

  const { width } = useWindowSize()

  const renderFlaredArrowIcon = () => {
    if (width && width >= theme.metrics.tablet) {
      return (
        <FlaredArrow
          fill={theme.colors.white}
          width={28}
          height={28}
        />
      )
    }

    return null
  }

  return (
    <>
      <Head>
        <title>Discover Our Story | Selling Your Home | Who Is HomeLister</title>
        <meta
          name="description"
          content="Who Is HomeLister? Learn more about why you should trust HomeLister to sell your home quickly and earn more of your equity. We'll help you sell your home today!"
        />
        <meta
          name="keywords"
          content="Our Story, About Us, Lindsay McLean"
        />
        <meta
          name="og:description"
          property="og:description"
          content="Who Is HomeLister? Learn more about why you should trust HomeLister to sell your home quickly and earn more of your equity. We'll help you sell your home today!"
        />
      </Head>
      <HeroWithContentOverlay
        imgSrc={`${process.env.CDN_BASE_URL}/q_auto/amazon/assets/1440/why-homelister.jpg`}
        overlayPadding={20}
        desktopOverlayPadding={50}
        borderRadius={30}
        desktopBorderRadius={40}
        ariaLabel="Our Story"
      >
        <BannerText>Our Story</BannerText>
      </HeroWithContentOverlay>
      <PageContainer>
        <Container>
          <Spacer />
          <H2>A letter from Lindsay McLean, Co-founder and CEO of HomeLister</H2>
          <Paragraph>
            Several years ago, I sold my home and used the same agent who helped me buy in the past.
            I was excited to sell my first home, but quickly found myself doing most of the work - I
            was getting it ready to show (it’s hard to make a home look like no one lives in it!),
            coordinating schedules with agents, researching home prices in the area, and setting the
            initial sale price. I was surprised to learn that selling a home involved so many more
            administrative tasks than buying, which is much more about evaluating which home is
            right and how much is it worth.
          </Paragraph>
          <Paragraph>
            My home sold in less than a week to a buyer without an agent, which allowed my agent to
            keep the full 6% commission (called double-ending the deal). I was thrilled about the
            sale but ended up writing a huge commission check at closing, which worked out to many
            thousands of dollars per hour worked. That commission check was worth more than 25% of
            my equity in the home. That blew my mind!
          </Paragraph>
          <H2>I knew there must be a better way.</H2>
          <Paragraph>
            I wanted an online service that would list your home on the MLS and other popular real
            estate search websites, then walk you through the home selling process without costing
            you 25% of your equity. It didn’t exist, and that’s why HomeLister was born.
          </Paragraph>
          <Paragraph>
            HomeLister was built based on my experience in software design and real estate
            development, and on my cofounder, Jennifer Stein&apos;s experience as an attorney and
            real estate broker focused on interstate compliance. We aimed to build a home selling
            platform that would work across the country and make the home selling process simple.
          </Paragraph>
          <Paragraph>
            We achieved our goals. HomeLister saves you an incredible amount of money and gets your
            home sold. And it even provides you with a better selling experience. Our clients tell
            us that they felt more informed about the process, more connected to our team, and
            happier about their sale than they ever did with a local in-person agent. We hope you
            agree. We are proud to help you sell your home and keep your hard earned equity right
            where it should be - in your pocket!
          </Paragraph>
        </Container>
        <Spacer size={30} />
        <BackgroundCover
          imgSrc={`${process.env.CDN_BASE_URL}/q_auto/amazon/assets/1440/why_HL_OurMission.jpg`}
          height={345}
        >
          <Banner>
            <BannerText>Our Mission</BannerText>
          </Banner>
        </BackgroundCover>
        <AccentTextBanner>
          To make selling a home simple, transparent, affordable and enjoyable!
        </AccentTextBanner>
        <Spacer size={30} />
        <MaxWidthContainer data-testid="getStartedWhyHomelister">
          <RoundedButton
            text="Get Started"
            href={{
              pathname: '/listing/create',
              query: query as { [key: string]: string },
            }}
            fontSize={16}
            height={60}
            padding="0 40px"
            color={theme.colors.white}
            background={theme.colors.blue4}
            renderRightIcon={renderFlaredArrowIcon}
          />
        </MaxWidthContainer>
        <Container>
          <H2>Our Better Way</H2>
          <Paragraph>
            Selling a home is actually quite simple, but there are a lot of things to know and
            putting all that information together yourself can be daunting. That is why we created a
            standardized process on our website that walks you through the forms and processes each
            step of the way. We focus on providing you with honest, helpful information with a
            brokerage team always available to help when you need it.
          </Paragraph>
          <H3>Low Flat Fees and Never a Percentage</H3>
          <Paragraph>
            We believe you should pay for the amount of work we do for you, not for the size or
            price of your home.
          </Paragraph>
          <H3>We Only Represent Sellers</H3>
          <Paragraph>
            Sellers pay the realtor commissions. So you should decide how the home is sold.
          </Paragraph>
        </Container>
        <Spacer size={30} />
        <BackgroundCover
          imgSrc={`${process.env.CDN_BASE_URL}/q_auto/amazon/assets/1440/why_HL_PersonalizedForYou.jpg`}
          height={345}
        >
          <Banner>
            <BannerText>Personalized for You</BannerText>
          </Banner>
        </BackgroundCover>
        <Container>
          <Paragraph>
            Our services are entirely online and virtual, providing you with the tools to
            successfully sell your home, while keeping thousands of dollars of your money that you
            would otherwise pay in commissions. Our technology enables us to provide quality
            services at dramatically lower prices than local agents. Our unique platform has a
            step-by-step guided process to list your home and our service structure allows you to
            conduct your own showings or have buyers’ agents conduct showings on your behalf. We
            provide services and guidance that rival traditional agents, including reviewing offers,
            handling paperwork, and handling your negotiations - you choose the level of service you
            desire. And we’ll send out professional photographers and yard sign installers to your
            home. We even have an app so you can show your home virtually to buyers and their
            agents.
          </Paragraph>
          <ResultsContainer>
            <H2>The Results</H2>
            <BoxContainer>
              <Box>
                <H4>$21,000</H4>
                <H5>Average Savings Per Sale</H5>
              </Box>
              <Box>
                <H4>$48,300,000</H4>
                <H5>Total Client Savings</H5>
              </Box>
              <Box>
                <H4>4.8 / 5.0</H4>
                <H5>Our Zillow Rating. Our clients LOVE us!</H5>
              </Box>
            </BoxContainer>
            <Spacer size={20} />
            <BoldText>
              Give us a try, it&apos;s always low flat fees starting at $599. We look forward to
              helping you sell your home!
            </BoldText>
          </ResultsContainer>
        </Container>
        <Spacer size={30} />
        <MaxWidthContainer>
          <RoundedButton
            text="Create a free account now"
            href={{
              pathname: '/listing/create',
              query: query as { [key: string]: string },
            }}
            flex-baiss={430}
            fontSize={width && width <= theme.metrics.phone ? 12 : 16}
            height={60}
            padding={`0 ${width && width <= theme.metrics.phone ? 20 : 40}px`}
            color={theme.colors.white}
            background={theme.colors.blue4}
            renderRightIcon={renderFlaredArrowIcon}
          />
        </MaxWidthContainer>
        <Spacer size={30} />
        <FilledContainer>
          <H2>A little trivia...</H2>
          <Paragraph>
            Over the last 50 years, real estate prices have gone up more than the average American’s
            income. As that has happened, the standard 6% real estate commission has grown to be
            more and more outsized as a fee in relation to most people’s incomes.
          </Paragraph>
          <Paragraph>
            The average American now has about 20% equity in their home at sale. And since the real
            estate commissions come only out of your 20% equity (not out of the bank’s 80% equity),
            a traditional 6% commission would be equal to 30% of your equity (see math below!). That
            is too high a price to pay as a transaction cost to sell your home.
          </Paragraph>
          <p>$100,000 Home</p>
          <p>$20,000 Owner’s Equity ($80,000 Loan)</p>
          <p>$6,000 Realtor Commission</p>
          <HorizontalRule
            width={300}
            color="black"
          />
          <Paragraph bold>$6,000/$20,000 = 30%</Paragraph>
          <Spacer />
        </FilledContainer>
      </PageContainer>
    </>
  )
}

export default WhyHomelister
